import type { ToolErrorCode } from "./errors";

/** `docs/tool-surface.md`. Every tool result carries one of these. */
export type ToolStatus = "ok" | "awaiting_user" | "running" | "blocked";

export type ToolResult<T> =
  | { readonly status: "ok"; readonly data: T }
  | { readonly status: "awaiting_user"; readonly instruction: string }
  | { readonly status: "running" }
  | { readonly status: "blocked"; readonly inFlight: string; readonly instruction: string }
  | {
      readonly status: "blocked";
      readonly error: { readonly code: ToolErrorCode; readonly message: string };
    };

/**
 * A successful result.
 *
 * ⚠️ PASS `null`, NEVER `undefined`, for a void tool. `src/safety/` rejects
 * explicit `undefined` by design: as an object property the key is silently
 * dropped and the result then fails its own declared JSON Schema with nothing
 * to say so. This is not theoretical — `flyTo` resolves `undefined` host-side
 * (verified against the current build 2026-09-09), so the seam normalises it.
 */
export function ok<T>(data: T): ToolResult<T> {
  return { status: "ok", data };
}

/** A tool that needs a person. Returns PROMPTLY — it never holds the frame. */
export function awaitingUser(instruction: string): ToolResult<never> {
  return { status: "awaiting_user", instruction };
}

/**
 * A second concurrent call, refused rather than queued (the refuse-don't-queue rule, `docs/error-taxonomy.md` § 2).
 *
 * The instruction says retry is safe, and that is a real distinction rather
 * than politeness: a call refused BEFORE IT STARTED is confirmably not
 * started, which is the exact opposite of a transport dropped mid-call —
 * that is `session_lost` and must never be retried.
 */
export function blockedBy(inFlight: string): ToolResult<never> {
  return {
    status: "blocked",
    inFlight,
    instruction: `A call to "${inFlight}" is still running. This call was refused before it started, so it is safe to retry.`,
  };
}

/** The tool did not run, or ran and could not report honestly. */
export function failed(code: ToolErrorCode, message: string): ToolResult<never> {
  return { status: "blocked", error: { code, message } };
}
